import { useEffect, useState, useCallback } from "react";
import { Form, Input, Upload, Button, Descriptions, Table } from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";

import { uploadFile } from "../../api/file";

import { compressImage } from "../../lib/utils";

import TextArea from "antd/es/input/TextArea";
import ClientAttachmentModal from "../../components/client/ClientAttachmentModal";
import ClientContactModal from "../../components/client/ClientContactModal";

export default function ClientForm({ onFormInstanceReady }) {
  const [form] = Form.useForm();
  const [showAttachmentsModal, setShowAttachementsModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [viewAttachments, setViewAttachments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContactsModal, setShowContactsModal] = useState(false);

  const openAttachmentsModal = () => {
    setShowAttachementsModal(true);
  };

  const closeAttachmentsModal = () => {
    setShowAttachementsModal(false);
  };

  const openContactModal = () => {
    setShowContactsModal(true);
  };

  const closeContatModal = () => {
    setShowContactsModal(false);
  };

  useEffect(() => {
    onFormInstanceReady(form);
    setAttachments(form.getFieldValue("attachments") || []);
    const c = form.getFieldValue("contacts") || [];
    setContacts(c.filter((contact) => contact.isActive));
  }, [form, onFormInstanceReady]);

  const contactColumn = [
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
    },
  ];

  const updateViewAttachments = useCallback(() => {
    const header = [
      {
        key: "1",
        label: "Attachments",
        labelStyle: { border: "0" },
        contentStyle: { backgroundColor: "rgba(0, 0, 0, 0.02)" },
        span: 2,
      },
      {
        key: "2",
        label: (
          <Button
            className="px-2"
            onClick={openAttachmentsModal}
            icon={<UploadOutlined />}
          >
            Click to Upload
          </Button>
        ),
        labelStyle: { border: "0" },
        contentStyle: { backgroundColor: "rgba(0, 0, 0, 0.02)" },
        span: 1,
      },
    ];

    attachments.map((attachment, index) => {
      return header.push(
        {
          key: `${attachment.id + index + 1}`,
          label: "Document Name",
          children: attachment.name || "N/A",
        },

        {
          key: `${attachment.id + index + 3}`,
          label: "Document",
          children: attachment.file.name,
          span: 2,
        }
      );
    });

    setViewAttachments(header);
  }, [attachments]);

  useEffect(() => {
    updateViewAttachments();
  }, [updateViewAttachments]);

  const uploadProps = {
    beforeUpload: async (file) => {
      const compressedFile = await compressImage(file);
      return compressedFile;
    },
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const res = await uploadFile(file, { entity: "client" });
        onSuccess(res);
      } catch (err) {
        onError(err);
      }
    },
    listType: "picture-card",
    multiple: false,
    maxCount: 1,
    accept: "image/*",
  };

  return (
    <div>
      <Form form={form} layout="vertical" className="w-1/2">
        <div className="grid grid-cols-2 gap-x-4 ">
          <Form.Item
            name={"avatar"}
            label="Upload Profile Picture"
            valuePropName="fileList"
            getValueFromEvent={(e) => e.fileList}
            className="col-span-2"
          >
            <Upload {...uploadProps}>
              <PlusOutlined className="mr-1" />
              Upload
            </Upload>
          </Form.Item>

          <Form.Item name={"name"} label="Name" required>
            <Input />
          </Form.Item>

          <Form.Item name={"tradingName"} label="Trading Name">
            <Input />
          </Form.Item>

          <Form.Item name={"addressName"} label="Address Name" required>
            <Input />
          </Form.Item>

          <Form.Item name={"street"} label="Street">
            <Input />
          </Form.Item>

          <Form.Item name={"state"} label="State">
            <Input />
          </Form.Item>

          <Form.Item name={"country"} label="Country" required>
            <Input />
          </Form.Item>

          <Form.Item name={"addressLine1"} label="Address Line 1">
            <Input />
          </Form.Item>

          <Form.Item name={"addressLine2"} label="Address Line 2">
            <Input />
          </Form.Item>

          <Form.Item name={"googlePlaceId"} label="Google ID">
            <Input />
          </Form.Item>

          <div className="grid grid-cols-2 gap-2">
            <Form.Item name={"lat"} label="Latitude">
              <Input />
            </Form.Item>
            <Form.Item name={"long"} label="Longitude">
              <Input />
            </Form.Item>
          </div>

          <Form.Item
            name={"description"}
            label="Description"
            className="col-span-2"
          >
            <TextArea />
          </Form.Item>

          <Form.Item value={contacts} name="contacts" className="h-0 m-0" />
          <Form.Item
            value={attachments}
            name="attachments"
            className="h-0 m-0"
          />
        </div>
      </Form>

      <ClientAttachmentModal
        open={showAttachmentsModal}
        close={closeAttachmentsModal}
        entity={"user"}
        onSave={(values) => {
          form.setFieldValue("attachments", values);
          setAttachments(values);
        }}
        attachments={attachments}
      />

      <ClientContactModal
        open={showContactsModal}
        close={closeContatModal}
        onSave={(values) => {
          form.setFieldValue("contacts", values);
          setContacts(values);
        }}
        contacts={contacts}
      />

      <Table
        bordered
        title={() => (
          <div className="flex justify-between items-center">
            <div>Contacts</div>
            <Button icon={<PlusOutlined />} onClick={openContactModal}>
              Add
            </Button>
          </div>
        )}
        columns={contactColumn}
        dataSource={contacts}
        className="pb-6"
        pagination={false}
      />

      <Descriptions bordered items={viewAttachments} className="pb-6" />
    </div>
  );
}
